import { Color, InstancedMesh, Matrix4, Mesh, Object3D, DynamicDrawUsage, InstancedBufferAttribute } from 'three';
import { IV3D } from '@/Interfaces/v3d';

declare let v3d: IV3D;

export class InstancingManager {
    private static instance: InstancingManager;
    private instances: Map<string, InstancedMesh> = new Map();
    private meshData: Map<string, { name: string, geometry: any, material: any, count: number, instances: Map<Object3D, Mesh>, needsUpdate: boolean }> = new Map();
    private scene: Object3D | null = null;
    private _needsUpdate = false;

    private constructor() {}

    public static getInstance(): InstancingManager {
        if (!InstancingManager.instance) {
            InstancingManager.instance = new InstancingManager();
        }
        return InstancingManager.instance;
    }

    public setScene(scene: Object3D) {
        this.scene = scene;
    }

    public setNeedsUpdate() {
        this._needsUpdate = true;
    }

    public registerInstance(originalMesh: Mesh, owner: Object3D) {
        const key = this.getMeshKey(originalMesh);
        if (!this.meshData.has(key)) {
            this.meshData.set(key, {
                name: this.getMeshName(originalMesh, owner),
                geometry: originalMesh.geometry,
                material: originalMesh.material,
                count: 0,
                instances: new Map(),
                needsUpdate: true,
            });
        }
        const data = this.meshData.get(key)!;
        data.instances.set(owner, originalMesh);
        data.needsUpdate = true;
        this._needsUpdate = true;
        this.updateInstancedMesh(key);
    }

    public unregisterInstance(originalMesh: Mesh, owner: Object3D) {
        const key = this.getMeshKey(originalMesh);
        const data = this.meshData.get(key);
        if (data) {
            data.instances.delete(owner);
            data.needsUpdate = true;
            this._needsUpdate = true;
            this.updateInstancedMesh(key);
        }
    }

    private getMeshKey(mesh: Mesh): string {
        // Уникальный ключ для комбинации геометрии и материала
        return `${mesh.geometry.uuid}_${(mesh.material as any).uuid}`;
    }

    private getMeshName(mesh: Mesh, owner: Object3D): string {
        // Пытаемся получить имя из владельца (Part или его наследники)
        const part = (owner as any).userData?.parent;
        if (part && part.constructor && part.constructor.name) {
            return part.constructor.name;
        }

        if (mesh.name && !mesh.name.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
            return mesh.name;
        }
        if ((mesh.geometry as any).name) {
            return (mesh.geometry as any).name;
        }
        return 'unnamed mesh';
    }

    private updateInstancedMesh(key: string, force = false) {
        const data = this.meshData.get(key)!;
        if (!data.needsUpdate && !force) return;

        let iMesh = this.instances.get(key);

        if (iMesh && iMesh.count !== data.instances.size) {
            this.scene?.remove(iMesh);
            iMesh.dispose();
            iMesh = undefined;
        }

        if (data.instances.size === 0) {
            if (iMesh) {
                this.scene?.remove(iMesh);
                iMesh.dispose();
            }
            this.instances.delete(key);
            return;
        }

        if (!iMesh) {
            iMesh = new InstancedMesh(data.geometry, data.material, data.instances.size);
            iMesh.instanceMatrix.setUsage(DynamicDrawUsage);
            this.instances.set(key, iMesh);
            this.scene?.add(iMesh);
        }
        
        let idx = 0;
        const color = new Color();

        data.instances.forEach((mesh, owner) => {
            // owner - это this.model из Part
            // mesh - это оригинальный меш (this._model из Part)
            if (mesh) {
                // Матрица меша относительно его родителя (owner)
                mesh.updateMatrix();
                
                // Мировая матрица родителя
                owner.updateMatrixWorld(true);
                
                // Вычисляем мировую матрицу меша: owner.matrixWorld * mesh.matrix
                const worldMatrix = new Matrix4().multiplyMatrices(owner.matrixWorld, mesh.matrix);
                
                iMesh!.setMatrixAt(idx, worldMatrix);
                
                // Обработка цвета если есть
                const part = (owner as any).userData.parent;
                if (part && part.color) {
                    color.set(`#${part.color.replace('#','')}`);
                    if (typeof iMesh!.setInstanceColorAt === 'function') {
                        iMesh!.setInstanceColorAt(idx, color);
                    } else {
                        // Резервный вариант для старых версий Verge3D/Three.js
                        if (!iMesh!.instanceColor) {
                            const colors = new Float32Array(data.instances.size * 3);
                            iMesh!.instanceColor = new InstancedBufferAttribute(colors, 3);
                        }
                        color.toArray(iMesh!.instanceColor.array, idx * 3);
                    }
                }
            }
            idx++;
        });

        iMesh.instanceMatrix.needsUpdate = true;
        if (iMesh.instanceColor) iMesh.instanceColor.needsUpdate = true;
        data.needsUpdate = false;
    }

    public updateAll() {
        if (!this._needsUpdate) return;
        this.meshData.forEach((_, key) => {
            this.updateInstancedMesh(key, true);
        });
        this._needsUpdate = false;
    }

    public printStats() {
        console.log('--- Instancing Stats ---');
        this.meshData.forEach((data) => {
            console.log(`Mesh: ${data.name}, Instances: ${data.instances.size}`);
        });
        console.log('------------------------');
    }
}
